<mat-card class="!p-6 border border-gray-3 shadow-sm bg-white dark:bg-gray-2">
  <div class="flex flex-col gap-4">
    <!-- Header -->
    <div>
      <h2 class="text-xl font-bold text-gray-12 m-0 flex items-center gap-2">
        <mat-icon class="!text-indigo-9">assignment_turned_in</mat-icon>
        <span>Candidature agent</span>
      </h2>
      <p class="text-gray-11 text-xs leading-relaxed mt-1 mb-0">
        Pour garantir la qualité de service, le propriétaire restreint l'accès à ce bien à un maximum de
        {{ maxCandidates() }} agents.
      </p>
    </div>

    <!-- Progress / Limit -->
    @let count = candidatesCount();
    @let max = maxCandidates();
    @let accepting = isAccepting();
    <div class="flex flex-col gap-2 bg-gray-1 dark:bg-gray-3/20 p-4 rounded-2xl border border-gray-2">
      <div class="flex justify-between text-sm font-semibold">
        <span class="text-gray-10">Agents retenus</span>
        <span [class]="count >= max || !accepting ? 'text-red-9' : 'text-gray-12'">
          {{ count }} / {{ max }}
        </span>
      </div>

      <!-- Progress Bar -->
      <div class="h-2.5 w-full bg-gray-3 rounded-full overflow-hidden">
        <div
          class="h-full rounded-full transition-all duration-500"
          [class.bg-indigo-9]="count < 3 && accepting"
          [class.bg-orange-9]="count >= 3 && count < max && accepting"
          [class.bg-red-9]="count >= max || !accepting"
          [style.width.%]="(count / max) * 100"
        ></div>
      </div>

      <!-- Status Info Badges -->
      <div class="mt-1 flex flex-col gap-1 text-xs">
        <div class="flex items-center gap-1.5">
          @if (!accepting || count >= max) {
            <mat-icon class="text-red-9 !text-sm size-4">lock</mat-icon>
            <span class="text-red-9 font-semibold">Les candidatures sont fermées</span>
          } @else {
            <mat-icon class="text-green-9 !text-sm size-4">lock_open</mat-icon>
            <span class="text-green-9 font-semibold">Opportunité ouverte aux propositions</span>
          }
        </div>
        @if (appliedCount(); as pending) {
          <span class="text-gray-10 text-[11px]">
            {{ pending }} candidature{{ pending > 1 ? 's' : '' }} en attente de réponse
          </span>
        }
      </div>
    </div>

    <!-- RAPPEL DU MANDAT & ATTENTES VENDEUR -->
    @if (property().mandate; as mandate) {
      <div
        class="rounded-2xl border border-indigo-4/60 bg-indigo-2/40 dark:bg-indigo-950/20 p-3.5 flex flex-col gap-2.5"
      >
        <div class="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-indigo-11">
          <mat-icon class="text-sm! w-4! h-4! !text-indigo-9">handshake</mat-icon>
          <span>Attentes du mandat</span>
        </div>

        <div class="grid grid-cols-2 gap-2 text-xs">
          <div class="bg-white/80 dark:bg-gray-1/80 p-2 rounded-xl border border-indigo-3/40">
            <span class="text-gray-10 block text-[10px] uppercase font-semibold">Type mandat</span>
            <span class="font-bold text-gray-12">{{ mandateTypeLabel() }}</span>
          </div>

          <div class="bg-white/80 dark:bg-gray-1/80 p-2 rounded-xl border border-indigo-3/40">
            <div class="flex items-center gap-1">
              <span class="text-gray-10 block text-[10px] uppercase font-semibold">Commission cible</span>
              <mat-icon
                class="text-xs! w-3.5! h-3.5! text-gray-9 hover:text-indigo-11 cursor-help"
                matTooltip="Taux de commission recherché par le vendeur. Vous êtes libre de candidater avec un taux différent ou supérieur."
                matTooltipPosition="above"
              >
                info
              </mat-icon>
            </div>
            <span class="font-bold text-indigo-11">
              @if (mandate.targetCommissionRate; as rate) {
                {{ rate }} %
              } @else {
                Libre
              }
            </span>
          </div>

          @if (mandateDurationLabel(); as dur) {
            <div class="bg-white/80 dark:bg-gray-1/80 p-2 rounded-xl border border-indigo-3/40 col-span-2">
              <span class="text-gray-10 block text-[10px] uppercase font-semibold">Durée souhaitée</span>
              <span class="font-bold text-gray-12">{{ dur }}</span>
            </div>
          }
        </div>

        <!-- Specific instructions -->
        @if (mandate.specialInstructions) {
          <div class="pt-1 border-t border-indigo-3/40 text-xs text-gray-11 italic">
            « {{ mandate.specialInstructions }} »
          </div>
        }
      </div>
    }

    <!-- Financial quick overview -->
    <div class="flex flex-col gap-2.5 py-3 border-y border-gray-3">
      <div class="flex justify-between items-center text-sm">
        <span class="text-gray-10">Prix net vendeur souhaité</span>
        <span class="font-bold text-gray-12">
          @if (sellingPriceEuros(); as price) {
            {{ price | number: '1.0-0' : 'fr-FR' }} €
          } @else {
            Non spécifié
          }
        </span>
      </div>

      <div class="flex justify-between items-center text-sm">
        <div class="flex items-center gap-1">
          <span class="text-gray-10">Honoraires ciblés</span>
          @if (targetCommissionRate(); as rate) {
            <span class="text-xs text-indigo-11 font-semibold">({{ rate }} %)</span>
          }
          <mat-icon
            class="text-xs! w-3.5! h-3.5! text-gray-9 hover:text-indigo-11 cursor-help"
            matTooltip="Montant indicatif d'honoraires selon le taux souhaité par le vendeur. Vous pouvez proposer votre propre barème."
            matTooltipPosition="above"
          >
            info
          </mat-icon>
        </div>
        <span class="font-bold text-indigo-11">
          @if (estimatedCommissionEuros(); as comm) {
            ~{{ comm | number: '1.0-0' : 'fr-FR' }} €
          } @else if (targetCommissionRate(); as rate) {
            {{ rate }} %
          } @else {
            Libre
          }
        </span>
      </div>

      @if (totalFaiEuros(); as totalFai) {
        <div
          class="flex justify-between items-center text-sm pt-2 border-t border-gray-2 dark:border-gray-3/40"
        >
          <div class="flex flex-col">
            <span class="font-bold text-gray-12">Prix total FAI</span>
            <span class="text-[10px] text-gray-10">Frais d'agence inclus</span>
          </div>
          <span class="font-extrabold text-base text-gray-12">
            {{ totalFai | number: '1.0-0' : 'fr-FR' }} €
          </span>
        </div>
      }
    </div>

    <!-- Action Buttons -->
    @if (!shouldHideActions()) {
      <div class="mt-2 flex flex-col gap-3">
        @if (isOwnerUser()) {
          <div
            class="flex items-center justify-center gap-2 p-3.5 rounded-xl bg-gray-2 border border-gray-4 text-gray-11 text-center"
          >
            <mat-icon class="size-5 !text-lg text-gray-9">info</mat-icon>
            <span class="text-sm font-medium">Votre annonce (Propriétaire)</span>
          </div>
        } @else if (isApplied()) {
          <div
            class="flex flex-col items-center justify-center p-3.5 rounded-xl bg-green-2 border border-green-5 text-green-11 text-center gap-2"
          >
            <div class="flex items-center gap-1.5 font-bold text-sm">
              <mat-icon class="size-5 !text-lg text-green-10">check_circle</mat-icon>
              <span>Candidature envoyée</span>
            </div>
            <p class="text-xs text-green-10 m-0">Le propriétaire a été notifié de votre intérêt.</p>
            <a
              mat-stroked-button
              [routerLink]="['/follow-up', property()._id]"
              class="w-full !py-4 font-bold text-xs mt-1"
            >
              Accéder au suivi du projet
            </a>
          </div>
        } @else if (!accepting || count >= max) {
          <div
            class="flex items-center justify-center p-3.5 rounded-xl bg-gray-2 border border-gray-4 text-gray-10 text-center text-sm font-medium"
          >
            <span>Candidatures fermées (limite atteinte)</span>
          </div>
        } @else if (!isSubscribed()) {
          <div
            class="flex flex-col items-center justify-center p-4 rounded-2xl bg-amber-2 border border-amber-6 text-center gap-2"
          >
            <div class="flex items-center gap-1.5 font-bold text-sm text-amber-12">
              <mat-icon class="size-5 !text-lg text-amber-10">lock</mat-icon>
              <span>Abonnement requis</span>
            </div>
            @if (hasBeenSubscribed()) {
              <p class="text-xs text-amber-11 m-0">
                Votre abonnement a expiré. Un abonnement actif est requis pour proposer vos services à ce
                vendeur.
              </p>
              <a
                mat-flat-button
                routerLink="/subscriptions"
                class="primary w-full !py-5 font-bold text-xs mt-1"
              >
                Renouveler mon abonnement
              </a>
            } @else {
              <p class="text-xs text-amber-11 m-0">
                Un abonnement actif est requis pour proposer vos services à ce vendeur et accéder à l'ensemble
                des opportunités de vente.
              </p>
              <a
                mat-flat-button
                routerLink="/subscriptions"
                class="primary w-full !py-5 font-bold text-xs mt-1"
              >
                Découvrir les abonnements
              </a>
            }
          </div>
        } @else {
          <button
            mat-flat-button
            class="primary w-full !py-6 font-bold hover:brightness-105 active:scale-[0.98] transition-all duration-200"
            (click)="openForm()"
          >
            Proposer mes services
          </button>
        }
      </div>
    }
  </div>
</mat-card>
